import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';

const FAQS = [
  {
    question: "What makes Villa Avoils' Cold Press Crude Avocado Oil different?",
    answer: "Our oil is cold-pressed directly from premium Hass avocados grown in the nutrient-rich soils of Nyeri, Kenya. We control the entire process from farm to bottle, ensuring zero chemical additives, maximum nutrient retention, and an authentic emerald green color."
  },
  {
    question: "Can I use your avocado oil for high-heat cooking?",
    answer: "Absolutely! Our Cold Press Crude Avocado Oil boasts one of the highest smoke points of any cooking oil (up to 271°C or 520°F). This makes it perfect for searing, frying, grilling, and roasting without breaking down or losing its nutritional value."
  },
  {
    question: "How does your 'zero-waste' philosophy work in practice?",
    answer: "We believe in utilizing every part of the avocado. While the premium flesh is cold-pressed for oil, the seeds and skins are repurposed into organic fertilizers and biomass energy, ensuring nothing goes to a landfill and everything returns to the earth."
  },
  {
    question: "Are your products sustainably and ethically sourced?",
    answer: "Yes. We partner directly with over 500 small-scale farmers in the Mt. Kenya region. By cutting out middlemen, we guarantee fair trade prices, provide agronomic training, and foster sustainable farming practices that regenerate the land and uplift the community."
  },
  {
    question: "Is your avocado oil suitable for cosmetic or skincare use?",
    answer: "Definitely. Because it is 100% pure and cold-pressed, it retains exceptionally high levels of Vitamin E, antioxidants, and essential fatty acids. This makes it an excellent, fast-absorbing natural moisturizer for skin and hair."
  },
  {
    question: "Do you accommodate bulk or international export orders?",
    answer: "Yes, we are fully equipped for global export. We offer wholesale pricing and bulk packaging options for international distributors, ensuring our premium oil reaches global markets safely, efficiently, and in compliance with international standards."
  }
];

export const FAQSection: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-16 bg-stone-50 relative overflow-hidden">
      <div className="max-w-4xl mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <div className="w-16 h-16 bg-avo-forest/10 text-avo-forest rounded-full flex items-center justify-center mx-auto mb-6">
            <HelpCircle size={32} />
          </div>
          <h2 className="text-4xl font-heading font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          <div className="h-1 w-20 bg-avo-gold mx-auto rounded-full mb-6"></div>
          <p className="text-gray-600 text-lg">
            Everything you need to know about our products, processes, and partnerships.
          </p>
        </div>

        <div className="space-y-4">
          {FAQS.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <div 
                key={index} 
                className={`bg-white border rounded-2xl transition-all duration-300 ${isOpen ? 'border-avo-forest shadow-md' : 'border-stone-200 shadow-sm hover:border-avo-gold/50'}`}
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none"
                >
                  <span className={`font-bold text-lg font-heading pr-4 ${isOpen ? 'text-avo-forest' : 'text-gray-900'}`}>
                    {faq.question}
                  </span>
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-transform duration-300 ${isOpen ? 'bg-avo-forest text-white rotate-180' : 'bg-stone-100 text-gray-500'}`}>
                    <ChevronDown size={20} />
                  </div>
                </button>
                <div 
                  className={`grid transition-all duration-300 ease-in-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
                >
                  <div className="overflow-hidden">
                    <p className="px-6 pb-6 text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
